// Email / SMTP settings domain for the admin (stored under the "smtp" setting).
import { getSetting, putSetting, apiRequest } from "../../lib/api";

export interface EmailSettings {
  enabled: boolean;
  sendWelcome: boolean;
  sendManagerNotice: boolean;
  sendReminders: boolean;
  reminderDays: number;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
  itContactEmail: string;
  itContactExt: string;
  hrContactEmail: string;
  hrContactExt: string;
  // Who gets copied on the manager-notification email — also carries the
  // pending-checklist-items list when the employee deferred any item.
  ccHrOnManagerNotice: boolean;
  ccItOnManagerNotice: boolean;
  // template HTML (empty → the server uses its built-in default)
  templateAr: string; templateEn: string;
  welcomeTemplateAr: string; welcomeTemplateEn: string;
  managerTemplateAr: string; managerTemplateEn: string;
  reminderTemplateAr: string; reminderTemplateEn: string;
  sendLinkTemplateAr: string; sendLinkTemplateEn: string;
  [key: string]: unknown;
}

export const DEFAULT_EMAIL_SETTINGS: EmailSettings = {
  enabled: true, sendWelcome: true, sendManagerNotice: true, sendReminders: true, reminderDays: 3,
  host: "", port: 587, secure: false, username: "", password: "", fromEmail: "", fromName: "Kaden Onboarding",
  itContactEmail: "it@kaden-sa.com", itContactExt: "310", hrContactEmail: "hr@kaden-sa.com", hrContactExt: "",
  ccHrOnManagerNotice: true, ccItOnManagerNotice: true,
  templateAr: "", templateEn: "", welcomeTemplateAr: "", welcomeTemplateEn: "", managerTemplateAr: "", managerTemplateEn: "",
  reminderTemplateAr: "", reminderTemplateEn: "", sendLinkTemplateAr: "", sendLinkTemplateEn: "",
};

export interface TemplateDef {
  ar: string;
  en: string;
  vars: string[];
  arField: keyof EmailSettings;
  enField: keyof EmailSettings;
}
export const TEMPLATE_DEFS: Record<string, TemplateDef> = {
  completion: { ar: "شهادة الإتمام", en: "Completion certificate", vars: ["NAME", "IT_EMAIL", "IT_EXT", "HR_EMAIL", "HR_EXT", "YEAR"], arField: "templateAr", enField: "templateEn" },
  welcome: { ar: "بريد الترحيب", en: "Welcome email", vars: ["NAME", "IT_EMAIL", "IT_EXT", "PORTAL_URL", "YEAR"], arField: "welcomeTemplateAr", enField: "welcomeTemplateEn" },
  manager: { ar: "إشعار المدير", en: "Manager notification", vars: ["NAME", "ROLE", "PATH", "DATE", "DURATION", "YEAR"], arField: "managerTemplateAr", enField: "managerTemplateEn" },
  reminder: { ar: "تذكير الإكمال", en: "Completion reminder", vars: ["NAME", "DAYS_AGO", "LAST_STEP", "PORTAL_URL", "YEAR"], arField: "reminderTemplateAr", enField: "reminderTemplateEn" },
  sendlink: { ar: "إرسال الرابط", en: "Send-link email", vars: ["PORTAL_URL", "YEAR"], arField: "sendLinkTemplateAr", enField: "sendLinkTemplateEn" },
};

export async function getEmailSettings(): Promise<EmailSettings> {
  const res = await getSetting<EmailSettings>("smtp");
  const value = (res.data as any)?.value ?? res.data;
  return value && typeof value === "object" ? { ...DEFAULT_EMAIL_SETTINGS, ...value } : { ...DEFAULT_EMAIL_SETTINGS };
}
export async function saveEmailSettings(settings: EmailSettings): Promise<{ ok: boolean; error?: string }> {
  const res = await putSetting("smtp", settings);
  return res.ok ? { ok: true } : { ok: false, error: res.error };
}
export function resetEmailSettings(): Promise<{ ok: boolean; error?: string }> {
  return saveEmailSettings({ ...DEFAULT_EMAIL_SETTINGS });
}

/** Send a test email using the server-saved SMTP settings. */
export async function testEmail(to: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiRequest<{ ok?: boolean; error?: string }>("/api/email", {
    method: "POST",
    body: JSON.stringify({ to, subject: "Kaden Onboarding, SMTP test", html: "<p>This is a test email from the Kaden Onboarding admin panel.</p>", templateKey: "test" }),
  });
  return res.ok && (res.data as any)?.ok !== false ? { ok: true } : { ok: false, error: (res.data as any)?.error || res.error || `HTTP ${res.status}` };
}

/** Replace {{VAR}} placeholders in a template. */
export function renderTemplate(html: string, vars: Record<string, string>): string {
  return (html || "").replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? `{{${key}}}`);
}
